import * as React from 'react';
import { useSelector } from 'react-redux';
import { Permissions } from '../../../models/enums/permissions';
import { validatePermission } from '../../../containers/authorization-helpers/validate-permission';
import { IApplicationState } from '../../../store/IApplicationState';
import { TransportationUtils } from '../../helpers';
import { ShipmentStatus, ShipmentStatusMetadata } from '../../models/enums/shipment-status';
import { ShipmentType } from '../../models/enums/shipment-type';
import { getShipmentDocumentTypes, ShipmentDocumentType } from '../../models/shipments/shipment-document-types';
import { ShipmentSummary } from '../../models/shipments/shipment-summary';
import { downloadDocumentActivity, moveToStatusActivity, ShipmentBulkActivity } from '../../shipment-bulk-activities';
import { TransportationSvc } from '../../service';
import { ShipmentBulkActivityFlyout } from './ShipmentBulkActivityFlyout';

// #region Component Props

interface IProps {

    /** The shipments that are selected in the list */
    shipments       : ShipmentSummary[];
    type            : ShipmentType;

    /** Asks for the list to be searched again, once what was done changed what is in it */
    onRefresh       : () => void;

    /** Tells when the flyout is open, which is when the list is not to be selected from */
    onBusyChange    : (isBusy : boolean) => void;

    children        : any;
}

interface IMenuPosition {
    x : number;
    y : number;
}

// #endregion

// The rows that were selected are the ones the menu is for
const SELECTED_ROW_SELECTOR = 'tbody tr.selected-already';

/**
 * Lets the shipments that are selected in a list be acted on with a right click: moved to the status that is next for
 * all of them, which is there when they have the same one, or have a document of each downloaded.  What it does for each
 * of them is shown in a flyout as it goes.
 */
export const ShipmentBulkActions = (props: IProps) => {

    const [service]                         = React.useState(new TransportationSvc());
    const [menuPosition, setMenuPosition]   = React.useState<IMenuPosition>();
    const [activity, setActivity]           = React.useState<ShipmentBulkActivity>();
    const [flyoutShipments, setFlyoutShipments] = React.useState<ShipmentSummary[]>([]);
    const [isFlyoutOpen, setIsFlyoutOpen]   = React.useState(false);
    const menuRef                           = React.useRef<HTMLDivElement>(null);

    const currentPermissions = useSelector((state: IApplicationState) => state.userdata.user.permissions);

    const isTruck = props.type === ShipmentType.Truck;
    const editPermission = isTruck ? Permissions.ShippingTrucksEdit : Permissions.ShippingContainersEdit;
    const viewPermission = isTruck ? Permissions.ShippingTrucksView : Permissions.ShippingContainersView;

    // A shipment is moved on by someone who can edit, and the documents are for someone who can view
    const nextStatus : ShipmentStatus | undefined = TransportationUtils.getSharedNextStatus(props.shipments);
    const canMove = nextStatus !== undefined
        && validatePermission({ currentPermissions, requiredPermissions: [ editPermission ] });
    const canDownload = validatePermission({ currentPermissions, requiredPermissions: [ viewPermission ] });

    const hideMenu = () => setMenuPosition(undefined);

    /**
     * Show the menu for the selection when a selected row is right clicked.  A link keeps the menu of the browser, as
     * it is where it is opened in another tab from.
     */
    const onContextMenu = (e : React.MouseEvent) => {

        const target = e.target as HTMLElement;
        if (props.shipments.length === 0 || (!canMove && !canDownload)
            || !target.closest || !target.closest(SELECTED_ROW_SELECTOR) || target.closest('a')) {
            return;
        }

        e.preventDefault();
        setMenuPosition({ x: e.clientX, y: e.clientY });
    };

    // Put the menu where it was asked for, but all of it on the screen
    React.useLayoutEffect(() => {
        const menu = menuRef.current;
        if (menuPosition && menu) {
            const margin = 8;
            menu.style.left = `${ Math.max(margin, Math.min(menuPosition.x, window.innerWidth - menu.offsetWidth - margin)) }px`;
            menu.style.top = `${ Math.max(margin, Math.min(menuPosition.y, window.innerHeight - menu.offsetHeight - margin)) }px`;
        }
    }, [menuPosition]);

    // Away with the menu when anything else is clicked, or the page moves under it
    React.useEffect(() => {

        if (!menuPosition) {
            return;
        }

        const onMouseDown = (e : MouseEvent) => {
            if (!menuRef.current || !menuRef.current.contains(e.target as Node)) {
                hideMenu();
            }
        };

        const onKeyDown = (e : KeyboardEvent) => {
            if (e.key === 'Escape') {
                hideMenu();
            }
        };

        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKeyDown);
        window.addEventListener('scroll', hideMenu, true);
        window.addEventListener('resize', hideMenu);

        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('scroll', hideMenu, true);
            window.removeEventListener('resize', hideMenu);
        };
    }, [menuPosition]);

    const startActivity = (toStart : ShipmentBulkActivity) => {
        hideMenu();
        setActivity(toStart);
        setFlyoutShipments([...props.shipments]);
        setIsFlyoutOpen(true);
        props.onBusyChange(true);
    };

    const closeFlyout = (refresh : boolean) => {
        setIsFlyoutOpen(false);
        props.onBusyChange(false);
        if (refresh) {
            props.onRefresh();
        }
    };

    return <div onContextMenu={ onContextMenu }>

        { props.children }

        {
            menuPosition
                ? <div
                    ref={ menuRef }
                    role="menu"
                    className="right-click menu-container shipment-bulk-menu punchthrough"
                    style={{ left: menuPosition.x, top: menuPosition.y, opacity: 1 }}>

                    {
                        canMove && nextStatus !== undefined
                            ? <React.Fragment>
                                <p>Status</p>
                                <button
                                    role="menuitem"
                                    onClick={ () => startActivity(moveToStatusActivity(service, nextStatus)) }>
                                    <i className="la la-arrow-circle-right" />
                                    Move to { ShipmentStatusMetadata[nextStatus].display }
                                </button>
                            </React.Fragment>
                            : null
                    }

                    {
                        canDownload
                            ? <React.Fragment>
                                { canMove ? <div className="right-click-divide" /> : null }
                                <p>Download</p>
                                {
                                    getShipmentDocumentTypes(props.type).map((doc : ShipmentDocumentType) =>
                                        <button
                                            key={ doc.key }
                                            role="menuitem"
                                            onClick={ () => startActivity(downloadDocumentActivity(doc)) }>
                                            <i className={ `la ${ doc.icon }` } />
                                            { doc.display }
                                        </button>
                                    )
                                }
                            </React.Fragment>
                            : null
                    }
                </div>
                : null
        }

        <ShipmentBulkActivityFlyout
            isOpen={ isFlyoutOpen }
            onClose={ closeFlyout }
            shipments={ flyoutShipments }
            activity={ activity } />

    </div>;
};
